<script setup>
import { ref } from 'vue';
import { VueFlow, Position } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls } from '@vue-flow/controls';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';

// Vehicle 1: Global ETFs & US Stock UDRs
const vehicle1Nodes = ref([
  {
    id: 'v1-step1',
    type: 'default',
    position: { x: 20, y: 20 },
    data: { label: '📝 STEP 1: Open IFSC Trading & Demat' },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '11px',
      padding: '10px 14px',
      borderRadius: '8px',
      width: '200px',
      textAlign: 'center',
      boxShadow: '0 3px 12px rgba(102, 126, 234, 0.3)',
      whiteSpace: 'pre-wrap'
    }
  },
  {
    id: 'v1-step1-detail',
    type: 'default',
    position: { x: 240, y: 20 },
    data: { label: 'Platform: HDFC Securities IFSC | KYC: Digital (PAN, Aadhaar, Video) | Approval: 48-72 hrs' },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
      border: '2px solid #3b82f6',
      borderRadius: '8px',
      padding: '10px',
      width: '240px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '9.5px',
      color: '#1e3a8a',
      fontWeight: '500',
      boxShadow: '0 2px 10px rgba(59, 130, 246, 0.15)'
    }
  },
  {
    id: 'v1-step2',
    type: 'default',
    position: { x: 20, y: 100 },
    data: { label: '💰 STEP 2: Fund IBU Account' },
    sourcePosition: Position.Right,
    targetPosition: Position.Top,
    style: {
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '11px',
      padding: '10px 14px',
      borderRadius: '8px',
      width: '200px',
      textAlign: 'center',
      boxShadow: '0 3px 12px rgba(16, 185, 129, 0.3)'
    }
  },
  {
    id: 'v1-step2-detail',
    type: 'default',
    position: { x: 240, y: 100 },
    data: { label: 'Transfer USD to GIFT City IBU FCA | Purpose Code: S0001 | TCS: 20% on >₹10L/year' },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
      border: '2px solid #10b981',
      borderRadius: '8px',
      padding: '10px',
      width: '240px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '9.5px',
      color: '#064e3b',
      fontWeight: '500',
      boxShadow: '0 2px 10px rgba(16, 185, 129, 0.15)'
    }
  },
  {
    id: 'v1-step3',
    type: 'default',
    position: { x: 20, y: 180 },
    data: { label: '📊 STEP 3: Execute Trade' },
    sourcePosition: Position.Right,
    targetPosition: Position.Top,
    style: {
      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '11px',
      padding: '10px 14px',
      borderRadius: '8px',
      width: '200px',
      textAlign: 'center',
      boxShadow: '0 3px 12px rgba(245, 158, 11, 0.3)'
    }
  },
  {
    id: 'v1-step3-detail',
    type: 'default',
    position: { x: 240, y: 180 },
    data: { label: 'UDR: AAPL, MSFT | ETF: VOO, QQQ | Timing: 🕗 7:30 PM – 2:00 AM IST (🇺🇸 market hours)' },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      border: '2px solid #f59e0b',
      borderRadius: '8px',
      padding: '10px',
      width: '240px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '9.5px',
      color: '#92400e',
      fontWeight: '500',
      boxShadow: '0 2px 10px rgba(245, 158, 11, 0.15)'
    }
  }
]);

const vehicle1Edges = ref([
  { id: 'v1-e1', source: 'v1-step1', target: 'v1-step1-detail', animated: true, style: { stroke: '#3b82f6', strokeWidth: 2 }, type: 'smoothstep', markerEnd: { type: 'arrowclosed', color: '#3b82f6' } },
  { id: 'v1-e2', source: 'v1-step1-detail', target: 'v1-step2', animated: true, style: { stroke: '#10b981', strokeWidth: 2 }, type: 'smoothstep', markerEnd: { type: 'arrowclosed', color: '#10b981' } },
  { id: 'v1-e3', source: 'v1-step2', target: 'v1-step2-detail', animated: true, style: { stroke: '#10b981', strokeWidth: 2 }, type: 'smoothstep', markerEnd: { type: 'arrowclosed', color: '#10b981' } },
  { id: 'v1-e4', source: 'v1-step2-detail', target: 'v1-step3', animated: true, style: { stroke: '#f59e0b', strokeWidth: 2 }, type: 'smoothstep', markerEnd: { type: 'arrowclosed', color: '#f59e0b' } },
  { id: 'v1-e5', source: 'v1-step3', target: 'v1-step3-detail', animated: true, style: { stroke: '#f59e0b', strokeWidth: 2 }, type: 'smoothstep', markerEnd: { type: 'arrowclosed', color: '#f59e0b' } }
]);

// Vehicle 2: GIFT City Active Funds (USD)
const vehicle2Nodes = ref([
  {
    id: 'v2-step1',
    type: 'default',
    position: { x: 520, y: 20 },
    data: { label: '📝 STEP 1: AMC Onboarding' },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '11px',
      padding: '10px 14px',
      borderRadius: '8px',
      width: '200px',
      textAlign: 'center',
      boxShadow: '0 3px 12px rgba(139, 92, 246, 0.3)'
    }
  },
  {
    id: 'v2-step1-detail',
    type: 'default',
    position: { x: 740, y: 20 },
    data: { label: 'DSP/Edelweiss/Sundaram GIFT Portal | Subscribe: DSP World Gold or Global Innovation Fund' },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
      border: '2px solid #8b5cf6',
      borderRadius: '8px',
      padding: '10px',
      width: '240px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '9.5px',
      color: '#581c87',
      fontWeight: '500',
      boxShadow: '0 2px 10px rgba(139, 92, 246, 0.15)'
    }
  },
  {
    id: 'v2-step2',
    type: 'default',
    position: { x: 520, y: 100 },
    data: { label: '🔗 STEP 2: Link IBU Account' },
    sourcePosition: Position.Right,
    targetPosition: Position.Top,
    style: {
      background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '11px',
      padding: '10px 14px',
      borderRadius: '8px',
      width: '200px',
      textAlign: 'center',
      boxShadow: '0 3px 12px rgba(6, 182, 212, 0.3)'
    }
  },
  {
    id: 'v2-step2-detail',
    type: 'default',
    position: { x: 740, y: 100 },
    data: { label: 'Provide IFSC IBU account details (HDFC/ICICI) | Must flow from IBU, not INR account' },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #cffafe 0%, #a5f3fc 100%)',
      border: '2px solid #06b6d4',
      borderRadius: '8px',
      padding: '10px',
      width: '240px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '9.5px',
      color: '#164e63',
      fontWeight: '500',
      boxShadow: '0 2px 10px rgba(6, 182, 212, 0.15)'
    }
  },
  {
    id: 'v2-step3',
    type: 'default',
    position: { x: 520, y: 180 },
    data: { label: '💼 STEP 3: Subscribe & Allotment' },
    sourcePosition: Position.Right,
    targetPosition: Position.Top,
    style: {
      background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '11px',
      padding: '10px 14px',
      borderRadius: '8px',
      width: '200px',
      textAlign: 'center',
      boxShadow: '0 3px 12px rgba(236, 72, 153, 0.3)'
    }
  },
  {
    id: 'v2-step3-detail',
    type: 'default',
    position: { x: 740, y: 180 },
    data: { label: 'Transfer USD to AMC pool account | Allotment: Next Day NAV (USD) | Units in SoA or IFSC Demat' },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
      border: '2px solid #ec4899',
      borderRadius: '8px',
      padding: '10px',
      width: '240px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '9.5px',
      color: '#831843',
      fontWeight: '500',
      boxShadow: '0 2px 10px rgba(236, 72, 153, 0.15)'
    }
  }
]);

const vehicle2Edges = ref([
  { id: 'v2-e1', source: 'v2-step1', target: 'v2-step1-detail', animated: true, style: { stroke: '#8b5cf6', strokeWidth: 2 }, type: 'smoothstep', markerEnd: { type: 'arrowclosed', color: '#8b5cf6' } },
  { id: 'v2-e2', source: 'v2-step1-detail', target: 'v2-step2', animated: true, style: { stroke: '#06b6d4', strokeWidth: 2 }, type: 'smoothstep', markerEnd: { type: 'arrowclosed', color: '#06b6d4' } },
  { id: 'v2-e3', source: 'v2-step2', target: 'v2-step2-detail', animated: true, style: { stroke: '#06b6d4', strokeWidth: 2 }, type: 'smoothstep', markerEnd: { type: 'arrowclosed', color: '#06b6d4' } },
  { id: 'v2-e4', source: 'v2-step2-detail', target: 'v2-step3', animated: true, style: { stroke: '#ec4899', strokeWidth: 2 }, type: 'smoothstep', markerEnd: { type: 'arrowclosed', color: '#ec4899' } },
  { id: 'v2-e5', source: 'v2-step3', target: 'v2-step3-detail', animated: true, style: { stroke: '#ec4899', strokeWidth: 2 }, type: 'smoothstep', markerEnd: { type: 'arrowclosed', color: '#ec4899' } }
]);
</script>

<template>
  <div class="gift-etf-udr-workflow">
    <h2 class="workflow-title">GIFT City ETF & UDR Investment Vehicles</h2>
    <p class="workflow-intro">
      <strong>Two Investment Routes:</strong> Choose between passive ETF/UDR tracking or active USD fund management in GIFT City.
    </p>

    <!-- Two Workflows Stacked Vertically -->
    <div class="workflows-stack">
      <!-- Vehicle 1: Global ETFs & US Stock UDRs -->
      <div class="workflow-section">
        <h3 class="section-title">🌍 Vehicle 1: Global ETFs & US Stock UDRs</h3>
        <p class="section-subtitle">Best for: Passive index tracking (Vanguard S&P 500) or top 50 US blue-chips</p>

        <!-- Nuance Boxes -->
        <div class="nuance-boxes">
          <div class="nuance-box udr">
            <div class="nuance-header">
              <span class="nuance-icon">📄</span>
              <strong>US Stock UDRs (NSE IX Receipts)</strong>
            </div>
            <p class="nuance-detail"><strong>The Nuance:</strong> These are "Local" versions of US stocks.</p>
            <p class="nuance-detail"><strong>Process:</strong> Buy them on NSE IX exchange using your IFSC Broker (HDFC Securities IFSC).</p>
            <p class="nuance-detail"><strong>Custody:</strong> Held in your CDSL-IFSC Demat. Highest protection route under Indian IFSC law.</p>
          </div>
          <div class="nuance-box etf">
            <div class="nuance-header">
              <span class="nuance-icon">📊</span>
              <strong>Global ETFs (Passive USD Tracking)</strong>
            </div>
            <p class="nuance-detail"><strong>The Nuance:</strong> These are "True" Global ETFs (like Vanguard S&P 500 - VOO) available on GIFT City exchanges.</p>
            <p class="nuance-detail"><strong>Process:</strong> Similar to UDRs, buy these on the exchange via your IFSC Broker.</p>
            <p class="nuance-detail"><strong>Difference from UDRs:</strong> While UDRs are "receipts" for individual stocks (like Apple), these are units of a massive USD-denominated basket.</p>
          </div>
        </div>

        <div class="workflow-container" style="height: 300px; border: 2px solid #e5e7eb; border-radius: 12px; background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%); box-shadow: 0 3px 16px rgba(0, 0, 0, 0.08);">
          <VueFlow
            :nodes="vehicle1Nodes"
            :edges="vehicle1Edges"
            :fit-view-on-init="false"
            :min-zoom="0.5"
            :max-zoom="1.5"
            :default-viewport="{ zoom: 1, x: 0, y: 0 }"
          >
            <Background pattern-color="#d1d5db" :gap="16" size="0.8" />
            <Controls />
          </VueFlow>
        </div>
      </div>

      <!-- Vehicle 2: GIFT City Active Funds -->
      <div class="workflow-section">
        <h3 class="section-title">💼 Vehicle 2: GIFT City Active Funds (USD)</h3>
        <p class="section-subtitle">Best for: Active alpha generation by Indian fund managers in tax-neutral USD zone</p>

        <!-- Nuance Box -->
        <div class="nuance-boxes">
          <div class="nuance-box active-funds">
            <div class="nuance-header">
              <span class="nuance-icon">💼</span>
              <strong>GIFT City Active Funds (USD Mutual Funds)</strong>
            </div>
            <p class="nuance-detail"><strong>The Nuance:</strong> These are USD-denominated mutual funds managed by Indian AMCs (like DSP or HDFC AMC) located in GIFT City.</p>
            <p class="nuance-detail"><strong>Process:</strong> You transfer USD from your IBU-FCA directly to the AMC's pool account in GIFT City.</p>
            <p class="nuance-detail"><strong>Best For:</strong> Investors who want professional management but want to stay within the tax-neutral GIFT zone.</p>
          </div>
        </div>

        <div class="workflow-container" style="height: 300px; border: 2px solid #e5e7eb; border-radius: 12px; background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%); box-shadow: 0 3px 16px rgba(0, 0, 0, 0.08);">
          <VueFlow
            :nodes="vehicle2Nodes"
            :edges="vehicle2Edges"
            :fit-view-on-init="false"
            :min-zoom="0.5"
            :max-zoom="1.5"
            :default-viewport="{ zoom: 1, x: 0, y: 0 }"
          >
            <Background pattern-color="#d1d5db" :gap="16" size="0.8" />
            <Controls />
          </VueFlow>
        </div>
      </div>
    </div>

    <!-- Key Comparison -->
    <div class="comparison-section">
      <h3>📊 Quick Comparison</h3>
      <div class="comparison-grid">
        <div class="comparison-card">
          <h4>🌍 ETFs & UDRs</h4>
          <ul>
            <li>✅ Low cost, passive tracking</li>
            <li>✅ 48-72 hour account setup</li>
            <li>✅ Trade during US market hours</li>
            <li>⚠️ Limited to popular stocks/indices</li>
          </ul>
        </div>
        <div class="comparison-card">
          <h4>💼 Active Funds</h4>
          <ul>
            <li>✅ Professional fund management</li>
            <li>✅ Tax-neutral USD zone</li>
            <li>✅ Next-day NAV allotment</li>
            <li>⚠️ Higher fees than passive ETFs</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.gift-etf-udr-workflow {
  max-width: 1400px;
  margin: 0 auto;
}

.workflow-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 6px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.workflow-intro {
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 16px;
  padding: 8px 12px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-left: 3px solid #3b82f6;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(59, 130, 246, 0.1);
}

.workflow-intro strong {
  color: #1e40af;
  font-weight: 700;
}

.workflows-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
}

.workflow-section {
  background: white;
  border-radius: 10px;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.section-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 4px 0;
}

.section-subtitle {
  font-size: 0.7rem;
  color: #6b7280;
  margin: 0 0 10px 0;
  font-style: italic;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nuance-boxes {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 10px;
}

.workflow-section:last-child .nuance-boxes {
  grid-template-columns: 1fr;
}

.nuance-box {
  background: linear-gradient(135deg, #fefce8 0%, #fef9c3 100%);
  border: 2px solid #fde047;
  border-radius: 8px;
  padding: 10px;
  font-size: 0.75rem;
}

.nuance-box.udr {
  background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
  border-color: #38bdf8;
}

.nuance-box.etf {
  background: linear-gradient(135deg, #ddd6fe 0%, #c4b5fd 100%);
  border-color: #a78bfa;
}

.nuance-box.active-funds {
  background: linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%);
  border-color: #f9a8d4;
}

.nuance-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.nuance-icon {
  font-size: 1rem;
}

.nuance-header strong {
  color: #1f2937;
  font-size: 0.8rem;
}

.nuance-detail {
  margin: 4px 0;
  color: #374151;
  line-height: 1.4;
}

.nuance-detail strong {
  color: #1f2937;
  font-weight: 600;
}

.comparison-section {
  margin-top: 16px;
  padding: 16px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 10px;
  border: 2px solid #bae6fd;
}

.comparison-section h3 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0c4a6e;
  margin: 0 0 12px 0;
}

.comparison-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.comparison-card {
  background: white;
  padding: 12px;
  border-radius: 8px;
  border: 2px solid #bae6fd;
  transition: all 0.3s ease;
}

.comparison-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(59, 130, 246, 0.15);
  border-color: #3b82f6;
}

.comparison-card h4 {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px 0;
}

.comparison-card ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.comparison-card ul li {
  padding: 4px 0;
  color: #4b5563;
  font-size: 0.75rem;
  line-height: 1.4;
}

@media (max-width: 1200px) {
  .workflows-stack {
    gap: 24px;
  }
}

@media (max-width: 768px) {
  .workflow-title {
    font-size: 1.5rem;
  }

  .comparison-grid {
    grid-template-columns: 1fr;
  }

  .workflow-container {
    height: 350px !important;
  }

  .workflows-stack {
    gap: 20px;
  }
}
</style>
